import { replaceWithRegion } from 'actions/NavigationActions'
import Pane from 'components/Common/Pane'
import PaletteSheet from 'LuxKit/PaletteSheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useOrder from 'hooks/Order/useOrder'
import useOffer from 'hooks/Offers/useOffer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { parseSearchString, getOccupanciesFromURLSearchParams } from 'lib/url/searchUrlUtils'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import AITripGenerating from 'tripPlanner/components/EntryPoints/AITripGenerating'
import AITripPreferences from 'tripPlanner/components/EntryPoints/AITripPreferences'
import AITripImageDisplay from 'tripPlanner/components/EntryPoints/AITripImageDisplay'
import GenerateTripFooter from 'tripPlanner/components/EntryPoints/Common/GenerateTripFooter'
import GenerateTripHeader from 'tripPlanner/components/EntryPoints/Common/GenerateTripHeader'
import { useLocationImage } from 'tripPlanner/hooks/api/locationImage'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { fallbackImageIds } from './AITripConstants'
import type { OccupancyOption } from './types'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ContactContext from 'contexts/contactContext'
import { useGenerateTripState } from './hooks/useGenerateTripState'
import { useStepNavigation } from './hooks/useStepNavigation'
import { useTripGeneration } from './hooks/useTripGeneration'
import GradientAnimatedBackground from 'tripPlanner/Tripkit/GradientAnimatedBackground'
import { isHotel } from 'lib/offer/offerTypes'
import { useCartHotelItems } from 'tripPlanner/hooks/api/cart'
import { useCityCountry } from 'tripPlanner/hooks/api/place'
import { getBackButtonConfig, getTripURL } from 'tripPlanner/utils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useTripGenerationData } from 'tripPlanner/hooks/api/tripGenerationData'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import type { TripGenerationFlow } from 'tripPlanner/components/EntryPoints/AITripStartSelection'
import { TRIP_GENERATION_FLOWS } from 'tripPlanner/components/EntryPoints/AITripStartSelection'
import TextButton from 'LuxKit/Button/TextButton'
import CreateTrip from 'tripPlanner/components/TripModal/Trips/CreateTrip/CreateTripMC'
import useNavigateTo from 'hooks/useNavigateTo'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { FullTrip } from 'tripPlanner/types/common'
import ModalContext from 'contexts/ModalContext'
import { rem } from 'polished'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import GeoContext from 'contexts/geoContext'

const StyledPane = styled(Pane)`
  min-height: calc(var(--vh) * 100);
  height: 100%;
  display: grid;
  grid-template: 
    "image" 0
    "content" 1fr / minmax(0, 1fr);
  
  ${mediaQueryUp.desktop} {
    grid-template: "content image" 1fr / 1fr 1fr;
  }
`

const StyledGradientAnimatedBackground = styled(GradientAnimatedBackground)`
  grid-area: content;
`

const ContentContainer = styled(Group)`
  width: 100%;
  grid-area: content;
  position: relative;

  max-height: 100vh;
  overflow-y: auto;
`

const EmptyTripGroup = styled(Group)`
padding: ${rem(20)};
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const extractOccupancyFromSearchParams = (urlSearchParams: URLSearchParams): OccupancyOption | undefined => {
  const occupancies = getOccupanciesFromURLSearchParams(urlSearchParams)

  if (occupancies && occupancies.length > 0) {
    const firstRoom = occupancies[0]
    const adults = isNaN(firstRoom.adults) ? 2 : firstRoom.adults

    const children = firstRoom.childrenAge?.filter(child => !isNaN(child)) || []

    return {
      adults,
      children,
    }
  }

  return undefined
}

function GenerateTripPage() {
  const intl = useIntl()
  const { search } = useLocation()

  const searchParams = useMemo(() => {
    return parseSearchString(search)
  }, [search])

  const {
    orderId,
    cartId,
    offerId: offerIdFromUrl,
    lat,
    lng,
    location: urlLocation,
    startDate: urlStartDate,
    endDate: urlEndDate,
  } = searchParams

  const urlSearchParams = useMemo(() => new URLSearchParams(search), [search])
  const occupancyFromUrl = useMemo(() => extractOccupancyFromSearchParams(urlSearchParams), [urlSearchParams])
  const [isSurpriseMe, setIsSurpriseMe] = useState(false)
  const [order, orderFetching, orderError] = useOrder(orderId)
  const { data: cartHotelItems, isLoading: cartHotelItemsFetching, error: cartError } = useCartHotelItems(cartId)
  const dispatch = useAppDispatch()
  const currentContact = useContext(ContactContext)
  const tripState = useGenerateTripState({
    startDate: offerIdFromUrl && urlStartDate ? urlStartDate : undefined,
    endDate: offerIdFromUrl && urlEndDate ? urlEndDate : undefined,
    occupancy: occupancyFromUrl,
  })
  const isLoggedIn = useAppSelector(selectLoggedIn)

  useEffect(() => {
    if (urlLocation && !tripState.location) {
      tripState.handleLocationSelect(urlLocation)
      if (!tripState.locations.some(loc => loc.locationName === urlLocation)) {
        tripState.handleAddLocationParams({ locationName: urlLocation })
      }
    }
  }, [urlLocation, tripState])
  const showModal = useContext(ModalContext)
  const navigateTo = useNavigateTo()
  const modalElement = useModalElementContext()

  const onCreateTrip = useCallback(
    (trip: FullTrip) => {
      navigateTo(getTripURL(trip.id))
      modalElement.resolve()
    },
    [modalElement, navigateTo],
  )
  const createNewEmptyTrip = useCallback(() => {
    showModal(<CreateTrip onCreateTrip={onCreateTrip} />)
  }, [showModal, onCreateTrip])

  const handleCreateNewEmptyTrip = usePendingLoginHandler(
    createNewEmptyTrip,
    'tripPlannerPagesLogin',
  )

  const cartHotelItem = cartHotelItems?.[0]
  const firstOfferId = offerIdFromUrl || cartHotelItem?.offerId || order?.items[0]?.offerId || order?.bedbankItems[0]?.offer.id
  const [firstOffer, firstOfferFetching, firstOfferError] = useOffer(firstOfferId)
  const {
    step,
    locations,
    selectedThemeNames,
    selectedMustDoActivityNames,
    tripPaces,
    proximity,
    foodStyle,
    activityBudget,
    dietaryRequirements,
    freeText,
    location,
    occupancy,
    datePicker,
    shouldGenerateActivities,
    analyticsType,
    analyticsUserChoices,
    newTripId,
    setStep,
    setShouldGenerateActivities,
    setAnalyticsType,
    setAnalyticsUserChoices,
    setNewTripId,
    handleThemeToggle,
    handlePaceSelect,
    handleProximitySelect,
    handleFoodStyleSelect,
    handleBudgetSelect,
    handleDietaryRequirementSelect,
    toggleMustDoActivity,
    handleLocationSelect,
    handleOccupancySelect,
    handleDatePickerSelect,
    handleAddLocationParams,
    handleRemoveLocation,
    handleUpdateAllLocationsOccupancy,
    handleLocationsReorder,
    handleLocationDurationChange,
    isAnyThemeSelected,
    isAnyMustDoActivitySelected,
    isAnyFoodStyleOrDietRequirementSelected,
  } = tripState

  useEffect(() => {
    if (isLoggedIn) {
      if ((orderError && !orderFetching) ||
        (cartError && !cartHotelItemsFetching) ||
        (firstOfferError && !firstOfferFetching)) {
        const invalidIdType = orderId ? 'order' : cartId ? 'cart' : offerIdFromUrl ? 'offer' : 'unknown'

        showSnackbar(
          intl.formatMessage(
            {
              id: 'ZEBH1YNm0X',
              defaultMessage: 'Something went wrong: invalid {invalidIdType} id',
              description: 'Error message shown when an invalid order, cart, or offer ID is provided during trip generation',
            },
            { invalidIdType },
          ),
          'success',
          {
            action: {
              label: intl.formatMessage(
                {
                  id: 'Ihk36/AOZ3',
                  defaultMessage: 'Call {contactNumber}',
                  description: 'Snackbar action label to call customer support phone number',
                },
                { contactNumber: currentContact.defaultContact.humanReadable },
              ),
              href: `tel:${currentContact.defaultContact.number}`,
            },
          },
        )

        if (offerIdFromUrl && !firstOffer) {
          dispatch(replaceWithRegion('/'))
        } else {
          dispatch(replaceWithRegion('/account/my-escapes'))
        }
      }
    }
  }, [orderError, orderFetching, cartError, cartHotelItemsFetching, firstOfferError, firstOfferFetching, firstOffer, dispatch, currentContact, isLoggedIn, orderId, cartId, offerIdFromUrl, intl])

  const firstAccommodationItem = firstOffer

  let accommodationLat: number | undefined
  let accommodationLng: number | undefined

  if (lat && lng) {
    accommodationLat = parseFloat(lat)
    accommodationLng = parseFloat(lng)
  } else if (firstAccommodationItem && isHotel(firstAccommodationItem)) {
    accommodationLat = firstAccommodationItem.property?.latitude
    accommodationLng = firstAccommodationItem.property?.longitude
  }

  const { city: hotelPlace, country: hotelCountryPlace } = useCityCountry({
    lat: accommodationLat,
    lng: accommodationLng,
  })

  const isPlaceFetching = !!(accommodationLat && accommodationLng && (!hotelPlace || !hotelCountryPlace))
  const hotelCity = hotelPlace?.name || ''

  const region = useContext(GeoContext).currentRegionCode

  // Combined hook for themes and must-do activities
  const tripGenerationData = useTripGenerationData({
    locations,
    region,
  })

  const themesV2 = tripGenerationData.themes
  const themesLoading = tripGenerationData.themesLoading

  const orderCartOfferLocation = useMemo(() => {
    return hotelCity || undefined
  }, [hotelCity])

  // Add location for offer/cart/order flows
  useEffect(() => {
    if (hotelCity && (offerIdFromUrl || orderId || cartId) && !locations.some(loc => loc.locationName === hotelCity)) {
      handleAddLocationParams({
        locationName: hotelCity,
        destinationId: hotelPlace?.id,
      })
    }
  }, [hotelCity, hotelPlace?.id, offerIdFromUrl, orderId, cartId, locations, handleAddLocationParams])

  const mustDoActivitiesByLocation = tripGenerationData.mustDoActivitiesByLocation

  const { totalSteps, stepNumbers, handleBack, handleNext, currentMustDoLocationIndex } = useStepNavigation({
    step,
    setStep,
    mustDoActivities: undefined,
    theme: themesV2,
    hasOrderId: !!orderId,
    hasCartId: !!cartId,
    hasOfferId: !!offerIdFromUrl,
    hasOrderCartOrOfferLocation: !!orderCartOfferLocation,
    urlLocation,
    isSurpriseMe,
    setIsSurpriseMe,
    locations,
    mustDoActivitiesByLocation,
  })

  const currentMustDoActivities = useMemo(() => {
    if (locations && locations.length > 0 && mustDoActivitiesByLocation && step.startsWith('mustDo')) {
      const currentLocation = locations[currentMustDoLocationIndex]
      if (currentLocation?.locationName) {
        const activities = mustDoActivitiesByLocation.get(currentLocation.locationName)
        return activities && activities.length > 0 ? activities : undefined
      }
    }
    return undefined
  }, [locations, mustDoActivitiesByLocation, step, currentMustDoLocationIndex])

  const currentMustDoLocationName = useMemo(() => {
    if (locations && locations.length > 0 && step.startsWith('mustDo')) {
      const currentLocation = locations[currentMustDoLocationIndex]
      return currentLocation?.locationName || hotelCity
    }
    return hotelCity
  }, [locations, step, currentMustDoLocationIndex, hotelCity])

  const { generateMultiDestinationTripMutation, handleGenerateTrip, handleTripSuccess } = useTripGeneration({
    orderId,
    cartId,
    offerIdFromUrl,
    urlLocation,
    hotelPlace,
    offer: firstOffer,
    setShouldGenerateActivities,
    setAnalyticsType,
    setAnalyticsUserChoices,
    setNewTripId,
  })

  const { data: trip } = useTrip({
    tripId: newTripId,
    onSuccess: (trip) => {
      handleTripSuccess(
        trip,
        shouldGenerateActivities,
        analyticsType,
        analyticsUserChoices,
        hotelPlace,
        hotelCity,
        locations,
      )
    },
  })

  const locationImagesQuery = useLocationImage(hotelPlace?.id)
  const { data: apiLocationImageIds } = locationImagesQuery
  const locationImageIds = apiLocationImageIds?.[0]?.imageIds?.length ? apiLocationImageIds[0].imageIds : fallbackImageIds

  const isLocationDataLoading =
    orderFetching ||
    cartHotelItemsFetching ||
    firstOfferFetching ||
    isPlaceFetching ||
    (!!hotelPlace?.id && locationImagesQuery.isLoading)

  const isGeneratingTrip = generateMultiDestinationTripMutation.isLoading ||
    (trip?.generationStatus === 'PENDING' || trip?.generationStatus === 'IN_PROGRESS')

  const handleSurpriseMeGenerateTripWithAuth = useCallback(() => {
    handleGenerateTrip(
      'non-customised',
      [],
      true,
      tripPaces,
      activityBudget,
      foodStyle,
      [],
      dietaryRequirements,
      proximity,
      '',
      location,
      occupancy,
      datePicker,
      locations,
    )
  }, [handleGenerateTrip, tripPaces, activityBudget, foodStyle, dietaryRequirements, proximity, location, occupancy, datePicker, locations])

  const handleSurpriseMeGenerateTrip = usePendingLoginHandler(
    handleSurpriseMeGenerateTripWithAuth,
    'tripPlannerPagesLogin',
  )
  const backButtonConfig = getBackButtonConfig({ cartId, orderId, offer: firstOffer })

  const handleSelectTripGenerationFlow = useCallback((flow: TripGenerationFlow) => {
    if (flow === TRIP_GENERATION_FLOWS.SURPRISE_ME) {
      setIsSurpriseMe(true)
    } else {
      setIsSurpriseMe(false)
    }
  }, [setIsSurpriseMe])

  return (
    <PaletteSheet paletteType="inverse">
      <StyledPane>
        {((!trip?.generationStatus && step === 'start') || trip?.generationStatus === 'PENDING') && <StyledGradientAnimatedBackground />}
        <ContentContainer direction="vertical" horizontalAlign="center" gap={20} tabletGap={40} fullWidth>
          {!trip?.generationStatus && (
            <>
              <GenerateTripHeader
                step={step}
                handleClick={handleBack}
                backButtonConfig={backButtonConfig}
              />
              <AITripPreferences
                hotelCity={currentMustDoLocationName}
                location={location}
                onLocationSelect={handleLocationSelect}
                locations={locations}
                onAddLocation={handleAddLocationParams}
                onRemoveLocation={handleRemoveLocation}
                onLocationsReorder={handleLocationsReorder}
                onLocationDurationChange={handleLocationDurationChange}
                occupancy={occupancy}
                onOccupancySelect={handleOccupancySelect}
                onUpdateAllLocationsOccupancy={handleUpdateAllLocationsOccupancy}
                datePicker={datePicker}
                onDatePickerSelect={handleDatePickerSelect}
                onGenerateTrip={handleGenerateTrip}
                isGeneratingTrip={isGeneratingTrip}
                step={step}
                stepNumbers={stepNumbers}
                totalSteps={totalSteps}
                mustDoActivities={currentMustDoActivities}
                themes={themesV2}
                themesLoading={themesLoading}
                handleNext={handleNext}
                selectedThemeNames={selectedThemeNames}
                onThemeToggle={handleThemeToggle}
                selectedMustDoActivityNames={selectedMustDoActivityNames}
                onMustDoActivityToggle={toggleMustDoActivity}
                tripPaces={tripPaces}
                onPaceSelect={handlePaceSelect}
                proximity={proximity}
                onProximitySelect={handleProximitySelect}
                foodStyle={foodStyle}
                onFoodStyleSelect={handleFoodStyleSelect}
                activityBudget={activityBudget}
                onBudgetSelect={handleBudgetSelect}
                dietaryRequirements={dietaryRequirements}
                onDietaryRequirementSelect={handleDietaryRequirementSelect}
                freeText={freeText}
                onFreeTextChange={tripState.setFreeText}
                hasUrlParams={!!(orderId || cartId || lat || lng)}
                urlLocation={urlLocation}
                isSurpriseMe={isSurpriseMe}
                onGenerateTripFlowSelect={handleSelectTripGenerationFlow}
                mustDoActivitiesByLocation={mustDoActivitiesByLocation}
              />
              {step === 'start' && (
                <EmptyTripGroup direction="vertical" gap={20} verticalAlign="end" fullHeight fullWidth>
                  <TextButton
                    kind="secondary"
                    fit="full-width"
                    onClick={handleCreateNewEmptyTrip}
                    endIcon={<LineAngleRightIcon />}
                  >
                    {intl.formatMessage({
                      id: 'FVZK6jUplq',
                      defaultMessage: 'Start from scratch',
                      description: 'Button text to create a new empty trip without AI generation',
                    })}
                  </TextButton>
                </EmptyTripGroup>
              )}
              {(step === 'location' || step === 'occupancy' || step === 'datePicker' || step === 'theme' || step === 'mustDo' || step.startsWith('mustDo') || step === 'foodStyle') &&
                <GenerateTripFooter
                  step={step}
                  isAnyThemeSelected={isAnyThemeSelected}
                  isAnyMustDoActivitySelected={isAnyMustDoActivitySelected}
                  isAnyFoodStyleOrDietRequirementSelected={isAnyFoodStyleOrDietRequirementSelected}
                  isGeneratingTrip={isGeneratingTrip}
                  handleClick={handleNext}
                  isLocationSelected={locations.length > 0}
                  isOccupancySelected={!!occupancy}
                  isDatePickerSelected={!!datePicker.startDate && !!datePicker.endDate && datePicker.startDate < datePicker.endDate}
                  isSurpriseMe={isSurpriseMe}
                  handleGenerateTrip={handleSurpriseMeGenerateTrip}
                />}
            </>
          )}
          {trip?.generationStatus === 'PENDING' && (
            <AITripGenerating hotelCity={hotelCity} />
          )}
        </ContentContainer>

        <AITripImageDisplay
          step={step}
          trip={trip}
          locationImageIds={locationImageIds}
          isLocationDataLoading={isLocationDataLoading}
        />
      </StyledPane>
    </PaletteSheet>
  )
}

export default GenerateTripPage
